
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Edit, Plus, Trash2 } from "lucide-react";
import { useState } from "react";
import { PlanDialog } from "./PlanDialog";
import { toast } from "@/components/ui/use-toast";

interface Plan {
  id: string;
  name: string;
  max_organizations: number;
  max_workspaces: number;
  max_boards_per_workspace: number;
  max_pomodoros_per_workspace: number;
  can_add_collaborators: boolean;
  can_share_with_external_users: boolean;
  is_active: boolean;
}

export const PlansManagement = () => {
  const [selectedPlan, setSelectedPlan] = useState<Plan | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const { data: plans, isLoading, refetch } = useQuery({
    queryKey: ['admin-plans'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('plans')
        .select('*')
        .is('deleted_at', null)
        .order('name');
      
      if (error) throw error;
      return data as Plan[];
    }
  });

  const handleStatusChange = async (planId: string, isActive: boolean) => {
    const { error } = await supabase
      .from('plans')
      .update({ is_active: isActive })
      .eq('id', planId);

    if (error) {
      toast({
        title: "Error",
        description: "No se pudo actualizar el estado del plan",
        variant: "destructive",
      });
    } else {
      toast({
        title: "Éxito",
        description: "Estado del plan actualizado",
      });
      refetch();
    }
  };

  const handleDelete = async (planId: string) => {
    const { error } = await supabase
      .from('plans')
      .update({ deleted_at: new Date().toISOString() })
      .eq('id', planId);

    if (error) {
      toast({
        title: "Error",
        description: "No se pudo eliminar el plan",
        variant: "destructive",
      });
    } else {
      toast({
        title: "Éxito",
        description: "Plan eliminado correctamente",
      });
      refetch();
    }
  };

  if (isLoading) {
    return <div>Cargando...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-3xl font-bold tracking-tight">Gestión de Planes</h2>
        <Button onClick={() => {
          setSelectedPlan(null);
          setIsDialogOpen(true);
        }}>
          <Plus className="h-4 w-4 mr-2" />
          Nuevo Plan
        </Button>
      </div>

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Nombre</TableHead>
              <TableHead>Organizaciones</TableHead>
              <TableHead>Workspaces</TableHead>
              <TableHead>Boards/Workspace</TableHead>
              <TableHead>Pomodoros/Workspace</TableHead>
              <TableHead>Colaboradores</TableHead>
              <TableHead>Usuarios Externos</TableHead>
              <TableHead>Activo</TableHead>
              <TableHead>Acciones</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {plans?.map((plan) => (
              <TableRow key={plan.id}>
                <TableCell>{plan.name}</TableCell>
                <TableCell>{plan.max_organizations}</TableCell>
                <TableCell>{plan.max_workspaces}</TableCell>
                <TableCell>{plan.max_boards_per_workspace}</TableCell>
                <TableCell>{plan.max_pomodoros_per_workspace}</TableCell>
                <TableCell>{plan.can_add_collaborators ? "Sí" : "No"}</TableCell>
                <TableCell>{plan.can_share_with_external_users ? "Sí" : "No"}</TableCell>
                <TableCell>
                  <Switch
                    checked={plan.is_active}
                    onCheckedChange={(checked) => handleStatusChange(plan.id, checked)}
                  />
                </TableCell>
                <TableCell>
                  <div className="flex space-x-2">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => {
                        setSelectedPlan(plan);
                        setIsDialogOpen(true);
                      }}
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDelete(plan.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <PlanDialog
        open={isDialogOpen}
        onOpenChange={setIsDialogOpen}
        plan={selectedPlan}
        onSuccess={() => {
          setIsDialogOpen(false);
          refetch();
        }}
      />
    </div>
  );
};
